/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import {
  ApiError,
  GuaranteeConfigStruture,
  PayloadRequestGarantias,
  PayloadRequestGarantiasVehicularInmobiliaria,
  PropertyTypeStrutureCatalog,
  StatusCatalog,
} from "@interfaces/index";
import ControlledPlaceAutocomplete from "@components/ControlledPlaceAutocomplete";
import { useEffect, useState } from "react";
import { useLazyGetPropertyTypeQuery } from "@api/catalogs";
import { useSelector } from "react-redux";
import {
  useDeleteGuaranteesTypesMutation,
  useLazyGetGuaranteesDataQuery,
  usePatchGuaranteesTypesMutation,
  usePostGuaranteesTypesMutation,
} from "@api/garantiasApi";
import { usePostValidateNextStageMutation, usePutApplicationCompletedMutation } from "@api/applications";
import { useNavigate } from "react-router-dom";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { setIsDisabledGuaranteeTypeSelect } from "@store/slices/registerSlice";

interface PropertyProps {
  type_inmobiliaria: string;
  valor_estimado: string;
  ubicacion: string;
  latitud: string;
  longitud: string;
  id?: string;
}

interface FormGarantiaInmobiliariaProps {
  nextStatus: StatusCatalog | null;
  guarantee_type_id: string | null;
  guaranteeConfigList: GuaranteeConfigStruture[];
  onSeleccion: (guarantee_type: string) => void;
  canAdd?: boolean;
}

const FormGarantiaInmobiliaria = ({
  nextStatus,
  guarantee_type_id,
  guaranteeConfigList,
  onSeleccion,
  canAdd,
}: FormGarantiaInmobiliariaProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const isDisabled = useSelector((state: RootState) => state.register.is_disabled_guarantee_type);

  const [triggerListPropertyType] = useLazyGetPropertyTypeQuery();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [postGuarantees] = usePostGuaranteesTypesMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [deleteGuarantees] = useDeleteGuaranteesTypesMutation();
  const [patchGuarantees] = usePatchGuaranteesTypesMutation();

  const [putApplicationCompleted] = usePutApplicationCompletedMutation();

  const emptyProperty: PropertyProps = {
    type_inmobiliaria: "",
    valor_estimado: "",
    ubicacion: "",
    latitud: "",
    longitud: "",
    id: undefined,
  };

  const [isLoading, setIsLoading] = useState(false);
  const [listPropertys, setListPropertys] = useState<PropertyTypeStrutureCatalog[]>([]);
  const [listPropertys_list, setListPropertys_list] = useState<PropertyTypeStrutureCatalog[]>([]);
  const [label, setLabel] = useState<string>("Inmueble");

  const formik = useFormik({
    initialValues: {
      properties: [{ ...emptyProperty }],
      guarantee_type: guarantee_type_id || "",
    },
    validationSchema: yup.object().shape({
      properties: yup.array().of(
        yup.object().shape({
          type_inmobiliaria: yup.string().required("Campo requerido"),
          valor_estimado: yup.string().required("Campo requerido"),
          ubicacion: yup.string().required("Campo requerido"),
        }),
      ),
      guarantee_type: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setIsLoading(true);
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        const guaranteesToPatch: Array<{ guarantee_id: string; data: PayloadRequestGarantiasVehicularInmobiliaria }> =
          [];
        const guaranteesToPost: PayloadRequestGarantiasVehicularInmobiliaria[] = [];

        values.properties.forEach((property) => {
          const cleanValue = Number(property.valor_estimado.replace(/,/g, "")) || 0;

          const guaranteeItem: PayloadRequestGarantiasVehicularInmobiliaria = {
            loan_application_id: application.id,
            property_type_id: property.type_inmobiliaria,
            estimated_value: cleanValue,
            latitude: Number(property.latitud),
            longitude: Number(property.longitud),
            address: property.ubicacion,
          };

          if (property?.id) {
            guaranteesToPatch.push({ guarantee_id: property.id, data: guaranteeItem });
          } else {
            guaranteesToPost.push(guaranteeItem);
          }
        });

        // Si existe id => PATCH (uno por garantía). Si no existe => POST (en lista)
        await Promise.all([
          ...guaranteesToPatch.map(({ guarantee_id, data }) =>
            patchGuarantees({ loan_application_id: application.id, guarantee_id, data }).unwrap(),
          ),
          guaranteesToPost.length > 0
            ? postGuarantees({
                loan_application_id: application.id,
                data: { guarantees_list: guaranteesToPost } as PayloadRequestGarantias,
              }).unwrap()
            : Promise.resolve(),
        ]);

        await putApplicationCompleted({
          id_sol: application.id,
          body: { guarantee_type_id: values.guarantee_type },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus?.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setIsDisabledGuaranteeTypeSelect({ disabled: true }));
        navigate(nextStatus?.url);
      } catch (error) {
        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "InfoGarantiaInmobiliaria",
            values: formik.values,
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const [response_property_type, response_garantia_data] = await Promise.all([
          triggerListPropertyType().unwrap(),
          getGuaranteesData({ loan_application_id: application.id }).unwrap(),
        ]);
        setListPropertys_list(response_property_type);

        if (response_garantia_data.length > 0) {
          const mappedProperties = response_garantia_data
            .filter((item) => item.property_type_id !== null && item.active)
            .map((item) => {
              const num = Number(item.estimated_value);
              const formatted = num.toLocaleString("en-US");

              return {
                type_inmobiliaria: item.property_type_id || "",
                valor_estimado: formatted || "0",
                latitud: String(item.latitude || ""),
                longitud: String(item.longitude || ""),
                ubicacion: item.address || "",
                id: item.id,
              };
            });

          if (mappedProperties.length > 0) {
            formik.setFieldValue("properties", mappedProperties);
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "Garantias-form-inmobiliaria",
            values: formik.values,
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const getFieldError = (fieldName: keyof PropertyProps, index: number): string | undefined => {
    const propertyErrors = formik.errors.properties as Array<Partial<Record<keyof PropertyProps, string>>> | undefined;
    if (propertyErrors && propertyErrors[index] && propertyErrors[index][fieldName]) {
      return propertyErrors[index][fieldName];
    }
    return undefined;
  };

  const handleAddProperty = () => {
    if (formik.values.properties.length < 5) {
      formik.setFieldValue("properties", [...formik.values.properties, emptyProperty]);
    }
  };

  const handleRemovePropertyAtIndex = async (indexToRemove: number) => {
    const propertyToRemove = formik.values.properties[indexToRemove];

    let canDelete = true;

    // Si tiene ID, primero intenta borrar en backend
    if (propertyToRemove?.id) {
      setIsLoading(true);
      try {
        await deleteGuarantees({
          loan_application_id: application.id,
          guarantee_id: propertyToRemove.id,
        }).unwrap();
      } catch (error) {
        canDelete = false;

        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "InfoGarantiaInmobiliaria",
            values: formik.values,
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    }

    // Solo elimina del form si se pudo borrar
    if (canDelete) {
      const newProperties = formik.values.properties.filter((_, index) => index !== indexToRemove);

      formik.setFieldValue("properties", newProperties);
    }
  };

  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(rawValue);
    const formattedValue = numericValue.toLocaleString("en-US");
    formik.setFieldValue(`properties[${index}].valor_estimado`, formattedValue);
  };

  const setListTypeInmobiliaria = (guarantee_type_id: string, list_default: PropertyTypeStrutureCatalog[]) => {
    const selectedGuarantee = guaranteeConfigList.find((item) => item.guarantee_type_id === guarantee_type_id);

    if (selectedGuarantee && selectedGuarantee.guarantee_type.code === "GCFC") {
      setLabel("Activo Fijo");
    }

    if (selectedGuarantee && selectedGuarantee.guarantee_type.code === "GTGPR") {
      setLabel("Activo Fijo");
    }

    return setListPropertys(list_default);
  };

  useEffect(() => {
    setListTypeInmobiliaria(formik.values.guarantee_type, listPropertys_list);
  }, [formik.values.guarantee_type, listPropertys_list]);

  return (
    <Box>
      <form onSubmit={formik.handleSubmit} noValidate>
        <FormControl
          fullWidth
          required
          sx={{
            ...customStylesAsterisk,
            mt: 2,
          }}
          error={formik.touched.guarantee_type && Boolean(formik.errors.guarantee_type)}
        >
          <InputLabel id="guarantee_type">Tipo de Garantía</InputLabel>
          <Select
            labelId="guarantee_type"
            id="guarantee_type"
            name="guarantee_type"
            fullWidth
            required
            label="Tipo de Garantía"
            value={formik.values.guarantee_type}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              formik.handleChange(e);
              onSeleccion(e.target.value);
            }}
            error={formik.touched.guarantee_type && Boolean(formik.errors.guarantee_type)}
            disabled={isLoading || formik.isSubmitting || isDisabled}
          >
            <MenuItem value={""}>Selecciona</MenuItem>
            {guaranteeConfigList.map((item) => (
              <MenuItem key={item.id} value={item.guarantee_type_id}>
                {item.guarantee_type.name}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.guarantee_type && formik.errors.guarantee_type && (
            <FormHelperText>{formik.errors.guarantee_type}</FormHelperText>
          )}
        </FormControl>

        <Typography
          mt={isMobileOrTablet ? 2 : 3}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.dark}
          textAlign={"center"}
        >
          Detalle de tu garantía
        </Typography>

        {formik.values.properties.map((property: PropertyProps, index: number) => (
          <Box key={index} mt={index > 0 ? 4 : 0}>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id={`type_inmobiliaria-label-${index}`} required>
                Tipo de {label}
              </InputLabel>
              <Select
                labelId={`type_inmobiliaria-label-${index}`}
                id={`type_inmobiliaria-${index}`}
                name={`properties[${index}].type_inmobiliaria`}
                fullWidth
                required
                label={`Tipo de ${label}`}
                value={property.type_inmobiliaria}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(
                  formik.touched.properties?.[index]?.type_inmobiliaria && getFieldError("type_inmobiliaria", index),
                )}
                disabled={formik.isSubmitting || isLoading}
              >
                <MenuItem value={""}>Selecciona</MenuItem>

                {listPropertys?.map((propertyType) => (
                  <MenuItem key={propertyType.id} value={propertyType.id}>
                    {propertyType.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.properties?.[index]?.type_inmobiliaria && getFieldError("type_inmobiliaria", index) && (
                <FormHelperText>{getFieldError("type_inmobiliaria", index)}</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id={`valor_estimado-${index}`}
                name={`properties[${index}].valor_estimado`}
                label="Valor Estimado"
                margin="normal"
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeValue(e, index)}
                inputProps={{ maxLength: 20 }}
                value={property.valor_estimado}
                disabled={formik.isSubmitting || isLoading}
                error={Boolean(
                  formik.touched.properties?.[index]?.valor_estimado && getFieldError("valor_estimado", index),
                )}
                helperText={
                  formik.touched.properties?.[index]?.valor_estimado ? getFieldError("valor_estimado", index) : ""
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </FormControl>

            <ControlledPlaceAutocomplete
              value={{ address: property.ubicacion, lat: 0, lng: 0 }}
              onChange={(newValue) => {
                formik.setFieldValue(`properties[${index}].ubicacion`, newValue.address);
                formik.setFieldValue(`properties[${index}].latitud`, newValue.lat);
                formik.setFieldValue(`properties[${index}].longitud`, newValue.lng);
              }}
              onBlur={() => formik.setFieldTouched(`properties[${index}].ubicacion`, true)}
              error={Boolean(formik.touched.properties?.[index]?.ubicacion && getFieldError("ubicacion", index))}
              helperText={formik.touched.properties?.[index]?.ubicacion ? getFieldError("ubicacion", index) : ""}
              disabled={formik.isSubmitting || isLoading}
            />

            {formik.values.properties.length > 1 && index !== 0 && (
              <Typography
                onClick={() => {
                  if (!isLoading) {
                    handleRemovePropertyAtIndex(index);
                  }
                }}
                color={isLoading ? "text.disabled" : theme?.palette?.primary?.main}
                fontSize="15px"
                fontWeight={400}
                textAlign="end"
                sx={{
                  cursor: isLoading ? "not-allowed" : "pointer",
                  mt: 1,
                  opacity: isLoading ? 0.6 : 1,
                  pointerEvents: isLoading ? "none" : "auto",
                }}
              >
                {isLoading ? "Eliminando..." : `- Eliminar ${label}`}
              </Typography>
            )}
          </Box>
        ))}
        {canAdd && (
          <>
            <Typography
              onClick={() => {
                if (!isLoading) {
                  handleAddProperty();
                }
              }}
              color={isLoading ? "text.disabled" : theme?.palette?.primary?.main}
              fontSize="15px"
              fontWeight={400}
              textAlign="end"
              sx={{
                cursor: isLoading ? "not-allowed" : "pointer",
                mt: 2,
                opacity: isLoading ? 0.6 : 1,
                pointerEvents: isLoading ? "none" : "auto",
              }}
            >
              {isLoading ? "Agregando..." : `+ Agregar otro ${label}`}
            </Typography>
          </>
        )}

        <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
          <Button
            variant="default"
            type="submit"
            color="primary"
            disabled={formik.isSubmitting || isLoading || !formik.isValid}
          >
            Continuar
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default FormGarantiaInmobiliaria;
